import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CertificateTable } from '../components/CertificateTable';
import { SearchFilter } from '../components/SearchFilter';
import { SkeletonTable } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import { certificatesAPI } from '../lib/api';
import { RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { AlertCircle, Info } from 'lucide-react';

export function CertificatesPage() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [certificates, setCertificates] = useState([]);
  const [filteredCerts, setFilteredCerts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadCertificates();
  }, []);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredCerts(certificates);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = certificates.filter(cert =>
        cert.domain.toLowerCase().includes(query) ||
        cert.server_source.toLowerCase().includes(query)
      );
      setFilteredCerts(filtered);
    }
  }, [searchQuery, certificates]);

  const loadCertificates = async () => {
    try {
      const response = await certificatesAPI.getAll();
      setCertificates(response.data);
      setFilteredCerts(response.data);
    } catch (error) {
      toast.error('Gagal memuat sertifikat');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadCertificates();
    setRefreshing(false);
    toast.success('Data sertifikat berhasil diperbarui');
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.25, 0.1, 0.25, 1]
      }
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="space-y-2">
            <div className="h-8 w-48 bg-muted animate-pulse rounded" />
            <div className="h-4 w-64 bg-muted animate-pulse rounded" />
          </div>
          <div className="h-10 w-32 bg-muted animate-pulse rounded" />
        </div>
        <div className="space-y-3">
          <div className="h-20 w-full bg-muted animate-pulse rounded" />
          <div className="h-20 w-full bg-muted animate-pulse rounded" />
        </div>
        <div className="h-12 w-full bg-muted animate-pulse rounded" />
        <SkeletonTable />
      </div>
    );
  }

  return (
    <motion.div 
      className="space-y-6"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Page Header */}
      <motion.div 
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
        variants={itemVariants}
      >
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">Sertifikat SSL</h2>
          <p className="text-muted-foreground mt-1 text-sm sm:text-base">
            Kelola dan monitor semua sertifikat SSL
          </p>
        </div>
        <motion.div
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <Button 
            onClick={handleRefresh} 
            disabled={refreshing}
            className="w-full sm:w-auto"
          >
            <motion.div
              animate={{ rotate: refreshing ? 360 : 0 }}
              transition={{ 
                duration: 1, 
                repeat: refreshing ? Infinity : 0,
                ease: "linear" 
              }}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
            </motion.div>
            Segarkan
          </Button>
        </motion.div>
      </motion.div>

      {/* Important Notices */}
      <motion.div className="space-y-3" variants={itemVariants}>
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <Alert className="border-orange-200 bg-orange-50">
            <AlertCircle className="h-4 w-4 text-orange-600" />
            <AlertTitle className="text-orange-900 text-sm sm:text-base">Perhatian Rate Limit</AlertTitle>
            <AlertDescription className="text-orange-800 text-xs sm:text-sm">
              Let's Encrypt membatasi pembaruan. Jika muncul error rate limit, tunggu 1-2 jam sebelum mencoba lagi.
            </AlertDescription>
          </Alert>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Alert className="border-blue-200 bg-blue-50">
            <Info className="h-4 w-4 text-blue-600" />
            <AlertTitle className="text-blue-900 text-sm sm:text-base">Tips Pembaruan</AlertTitle>
            <AlertDescription className="text-blue-800 text-xs sm:text-sm">
              Selalu lakukan <strong>Uji Pembaruan</strong> terlebih dahulu sebelum <strong>Pembaruan Live</strong> untuk memastikan tidak ada masalah.
            </AlertDescription>
          </Alert>
        </motion.div>
      </motion.div>

      {/* Search and Filter */}
      <motion.div 
        className="flex flex-col sm:flex-row gap-4 items-start sm:items-center"
        variants={itemVariants}
      >
        <div className="w-full sm:flex-1">
          <SearchFilter 
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Cari domain atau server..."
          />
        </div>
        <motion.div 
          className="text-sm text-muted-foreground whitespace-nowrap"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          {filteredCerts.length} dari {certificates.length} sertifikat
        </motion.div>
      </motion.div>

      {/* Certificates Table */}
      <motion.div variants={itemVariants}>
        <CertificateTable 
          certificates={filteredCerts} 
          onRefresh={handleRefresh}
        />
      </motion.div>
    </motion.div>
  );
}